import { Fragment } from "react";
import { Modal, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

// Translated messages may contain a constrained set of inline tags
// (<b>, <strong>, <br>) for emphasis/line breaks. Parsed here instead of via
// dangerouslySetInnerHTML so interpolated values (e.g. user-entered names)
// are always rendered as plain, escaped text and can never inject markup.
function renderSafeMessage(text) {
  if (typeof text !== "string") return text;
  const parts = text.split(/(<br\s*\/?>|<\/?(?:b|strong)>)/gi);
  const nodes = [];
  let bold = false;
  parts.forEach((part, idx) => {
    if (!part) return;
    if (/^<br\s*\/?>$/i.test(part)) {
      nodes.push(<br key={`br-${idx}`} />);
    } else if (/^<(?:b|strong)>$/i.test(part)) {
      bold = true;
    } else if (/^<\/(?:b|strong)>$/i.test(part)) {
      bold = false;
    } else {
      nodes.push(
        bold ? (
          <strong key={`t-${idx}`}>{part}</strong>
        ) : (
          <Fragment key={`t-${idx}`}>{part}</Fragment>
        )
      );
    }
  });
  return nodes;
}

// Mantine's default size-to-width mapping (approximate), used to keep the
// modal properly sized even when a global ".mantine-Paper-root" CSS rule
// elsewhere in the app (dataTable.scss) forces width/max-width with !important.
const MODAL_SIZE_PX = {
  xs: 320,
  sm: 380,
  md: 440,
  lg: 620,
  xl: 780,
};

const resolveContentWidth = (size) => {
  if (!size) return MODAL_SIZE_PX.md;
  if (typeof size === "number") return size;
  return MODAL_SIZE_PX[size] ?? MODAL_SIZE_PX.md;
};

const SEConfirmModal = ({
  opened,
  onClose,
  onConfirm,
  message,
  confirmLabel,
  cancelLabel,
  showCancel = true,
  showConfirm = true,
  header,
  confirmColor = "#3dcd58",
  confirmSeColor = "primary",
  cancelColor = "red",
  onlyOk = false,
  confirmDisabled = false,
  confirmLoading = false,
  confirmLoadingLabel,
  size,
  customStyles,
  modalPadding,
  // Optional third action button (e.g. "Save"), rendered alongside
  // Confirm/Cancel. Omitted entirely when onExtra is not provided.
  extraLabel,
  onExtra,
  extraDisabled = false,
  extraLoading = false,
  extraLoadingLabel,
  cancelDisabled = false,
  disableOutsideClickClose = false
}) => {
  const { t } = useTranslation();
  const effectiveMessage = message ?? t("common.confirmMessage");
  const effectiveConfirmLabel = confirmLabel ?? t("common.yes");
  const effectiveCancelLabel = cancelLabel ?? t("common.no");
  const effectiveHeader = header ?? t("common.alert");
  const contentWidth = resolveContentWidth(size);
  let closeOnClickOutsideModal = onlyOk;

  if(disableOutsideClickClose) {
    closeOnClickOutsideModal = true
  }

  return (
  <Modal
    opened={opened}
    onClose={onClose}
    title={null}
    centered
    withCloseButton={false}
    styles={{
      header: { padding: modalPadding ?? 0, margin: 0 },
      content: {
        position: "relative",
        overflow: "hidden",
        width: "100% !important",
        maxWidth: `min(${contentWidth}px, 92vw) !important`,
        height: "auto !important",
        ...(customStyles?.content || {}),
      },
    }}
    closeOnClickOutside={closeOnClickOutsideModal ? false : true}
  >
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: 56,
        background: '#3dcd58',
        color: '#fff',
        borderRadius: '6px 6px 0 0',
        fontWeight: 600,
        fontSize: 20,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '3%',
        zIndex: 2,
      }}
    >
      {effectiveHeader}
    </div>
    <div style={{ height: 56 }} />
    {typeof effectiveMessage === 'string' ? (
      <Text mb="md">{renderSafeMessage(effectiveMessage)}</Text>
    ) : (
      <div style={{ marginBottom: '1rem' }}>{effectiveMessage}</div>
    )}
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
      {onlyOk ? (
        <se-button aria-label="confirm-modal-ok" option="raised" color="standard" onClick={onClose}>{t("common.ok")}</se-button>
      ) : (
        <>
          {showConfirm && (
            <se-button
              aria-label="confirm-modal-confirm"
              option="flat"
              color={confirmSeColor}
              onClick={onConfirm}
              disabled={confirmDisabled || confirmLoading}
            >
              {confirmLoading
                ? (confirmLoadingLabel
                    ? confirmLoadingLabel
                    : <se-loading loading={true} style={{ height: '20px' }} />)
                : effectiveConfirmLabel}
            </se-button>
          )}
          {showCancel && (
            <se-button
              aria-label="confirm-modal-cancel"
              color="standard"
              // option="outline"
              onClick={onClose}
              disabled={cancelDisabled}
            >
              {effectiveCancelLabel}
            </se-button>
          )}
          {onExtra && (
            <button
              type="button"
              aria-label="confirm-modal-extra"
              onClick={onExtra}
              disabled={extraDisabled || extraLoading}
              style={{
                background: "#3dcd58",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "6px 16px",
                fontSize: 14,
                fontWeight: 500,
                cursor: extraDisabled || extraLoading ? "not-allowed" : "pointer",
                opacity: extraDisabled || extraLoading ? 0.6 : 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 32,
              }}
            >
              {extraLoading
                ? (extraLoadingLabel
                    ? extraLoadingLabel
                    : <se-loading loading={true} style={{ height: '20px' }} />)
                : extraLabel}
            </button>
          )}
        </>
      )}
    </div>
  </Modal>
  );
};

export default SEConfirmModal;
